import { cn } from "@bednbody/ui/lib/utils";
import {
  BicycleIcon,
  TrendDownIcon,
  TrendUpIcon,
  UsersIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import { useMemo } from "react";
import { minutesSince } from "@/lib/time";
import type { CommandBooking, useDispatchQueue } from "../command-center.hooks";
import { PRE_SESSION_STATUSES } from "../command-center.hooks";
import type { DailyBookingStat, useAttendance } from "../ops-dashboard.hooks";

type QueueRows = NonNullable<ReturnType<typeof useDispatchQueue>["data"]>;
type AttendanceRows = NonNullable<ReturnType<typeof useAttendance>["data"]>;

interface Signal {
  id: string;
  icon: Icon;
  tone: "info" | "warning" | "danger" | "success";
  text: string;
}

const TONE_STYLES: Record<Signal["tone"], string> = {
  info: "bg-info/15 text-info",
  warning: "bg-warning/15 text-warning",
  danger: "bg-destructive/15 text-destructive",
  success: "bg-success/15 text-success",
};

/**
 * Rule-based observations computed from the live queries on this page —
 * arithmetic over real rows, not an ML model, and labeled as such in the UI.
 * Each rule states a fact ("demand is 40% above yesterday"), never a policy
 * ("cancel X") — recommendations that imply authority are Open Items
 * (never-invent-policy.md).
 */
export function OpsSignalsCard({
  bookings,
  weekly,
  queue,
  attendance,
}: {
  bookings: CommandBooking[];
  weekly: DailyBookingStat[];
  queue: QueueRows;
  attendance: AttendanceRows;
}) {
  const signals = useMemo<Signal[]>(() => {
    const list: Signal[] = [];
    const today = weekly.at(-1);
    const yesterday = weekly.at(-2);

    if (today && yesterday && yesterday.bookings > 0) {
      const pct = Math.round(((today.bookings - yesterday.bookings) / yesterday.bookings) * 100);
      if (Math.abs(pct) >= 15) {
        list.push({
          id: "demand",
          icon: pct > 0 ? TrendUpIcon : TrendDownIcon,
          tone: pct > 0 ? "success" : "info",
          text: `Demand is ${Math.abs(pct)}% ${pct > 0 ? "above" : "below"} yesterday at the same point in the week (${today.bookings} vs ${yesterday.bookings} bookings).`,
        });
      }
    }

    const overdueByHub = new Map<string, number>();
    for (const b of bookings) {
      if (
        (PRE_SESSION_STATUSES as readonly string[]).includes(b.status) &&
        minutesSince(b.scheduled_at) > 30
      ) {
        const hub = b.hubs?.name ?? "Unknown hub";
        overdueByHub.set(hub, (overdueByHub.get(hub) ?? 0) + 1);
      }
    }
    const worstHub = [...overdueByHub.entries()].sort((a, b) => b[1] - a[1])[0];
    if (worstHub) {
      list.push({
        id: "delays",
        icon: WarningIcon,
        tone: "danger",
        text: `${worstHub[0]} has ${worstHub[1]} booking${worstHub[1] === 1 ? "" : "s"} more than 30 minutes past schedule without a session start.`,
      });
    }

    const availableRiders = queue.filter((r) => r.status === "available").length;
    const waiting = bookings.filter((b) => b.status === "awaiting_rider").length;
    if (waiting > 0 && availableRiders === 0) {
      list.push({
        id: "riders",
        icon: BicycleIcon,
        tone: "danger",
        text: `${waiting} booking${waiting === 1 ? "" : "s"} awaiting a rider and no riders are marked available.`,
      });
    } else if (availableRiders > 0 && waiting > availableRiders) {
      list.push({
        id: "riders-thin",
        icon: BicycleIcon,
        tone: "warning",
        text: `Rider supply is thin: ${waiting} bookings waiting against ${availableRiders} available rider${availableRiders === 1 ? "" : "s"}.`,
      });
    }

    const field = attendance.filter((a) => a.role === "therapist" || a.role === "rider");
    const onSession = field.filter((a) => a.attendance?.onDuty);
    const idle = onSession.filter((a) => !a.currentBooking).length;
    if (onSession.length >= 4 && idle / onSession.length > 0.7) {
      list.push({
        id: "utilization",
        icon: UsersIcon,
        tone: "info",
        text: `${idle} of ${onSession.length} on-session field staff are idle right now — utilization is low for this hour.`,
      });
    }

    return list;
  }, [bookings, weekly, queue, attendance]);

  if (signals.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        No notable signals — operations look steady.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <ul className="space-y-2">
        {signals.map((s) => {
          const IconComp = s.icon;
          return (
            <li
              key={s.id}
              className="flex items-start gap-3 rounded-xl border border-border bg-card px-3 py-2.5"
            >
              <span
                className={cn(
                  "mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full",
                  TONE_STYLES[s.tone],
                )}
              >
                <IconComp size={13} />
              </span>
              <p className="text-sm leading-snug text-foreground">{s.text}</p>
            </li>
          );
        })}
      </ul>
      <p className="text-right type-body-micro text-muted-foreground">
        derived from live data on this page — not a prediction model
      </p>
    </div>
  );
}
